/**
 * usePlaybackMachine - SM-1 回放主流程状态机
 *
 * Implements the SM-1 playback state machine using `useReducer` (pure reducer)
 * + `useContext` (Provider/consumer pattern). The reducer is a pure function
 * exported as {@link playbackReducer}; all side effects (rAF scheduling,
 * visibility-change handling, seek debounce) live in {@link PlaybackMachineProvider}.
 *
 * 使用 `useReducer`（纯 reducer）+ `useContext`（Provider/consumer 模式）实现
 * SM-1 回放主流程状态机。reducer 为纯函数 {@link playbackReducer}；所有副作用
 * （rAF 调度、visibilitychange 监听、seek 防抖）位于 {@link PlaybackMachineProvider}。
 *
 * States / 状态:
 *   Loading → Ready → Playing ⇄ PausedInTurn / PausedBetweenTurns → Ended
 *
 * Full transition table / 完整状态转移表:
 *   Loading.LoadingData + LOAD_SUCCESS  → Ready
 *   Loading.LoadingData + LOAD_ERROR    → Loading.LoadingError
 *   Ready              + USER_PLAY      → Playing
 *   Ready              + USER_SEEK      → Ready (update currentTimestamp)
 *   Playing            + USER_PAUSE     → PausedInTurn (if currentTurnIndex != null)
 *                                         or PausedBetweenTurns
 *   Playing            + USER_SEEK      → Playing (pendingSeek; applied via
 *                                         SEEK_DEBOUNCE after 50ms debounce)
 *   Playing            + FRAME_UPDATE   → Playing (update currentTimestamp)
 *   Playing            + PLAYBACK_ENDED → Ended
 *   Playing            + TAB_HIDDEN     → PausedInTurn or PausedBetweenTurns
 *                                         (cancel rAF)
 *   PausedInTurn       + USER_PLAY      → Playing
 *   PausedInTurn       + USER_SEEK      → PausedInTurn (update)
 *   PausedBetweenTurns + USER_PLAY      → Playing
 *   PausedBetweenTurns + USER_SEEK      → PausedBetweenTurns (update)
 *   Ended              + USER_SEEK      → Ready (if seek to non-end)
 *   Ended              + USER_PLAY      → Playing (restart from 0)
 *
 * Seek debouncing / seek 防抖:
 *   In the Playing state, USER_SEEK stores the timestamp in `pendingSeek`
 *   rather than applying it immediately. The Provider schedules a SEEK_DEBOUNCE
 *   event 50ms later; if another USER_SEEK arrives within that window, the
 *   previous timer is cleared and a new one starts, so only the final seek
 *   position is applied. This prevents mid-playback flicker from rapid seeks.
 *
 *   在 Playing 状态下，USER_SEEK 将时间戳存入 `pendingSeek` 而非立即应用。
 *   Provider 在 50ms 后调度 SEEK_DEBOUNCE 事件；若此期间内又收到 USER_SEEK，
 *   旧定时器被清除并重新计时，最终仅处理最后一次 seek 位置，避免快速 seek
 *   导致的画面闪烁。
 *
 * Current turn index / 当前弯序号:
 *   After every `currentTimestamp` update, the reducer recomputes
 *   `currentTurnIndex` by checking which turn's [start_ms, end_ms] contains
 *   the timestamp. When between turns:
 *     - In Paused* states: `currentTurnIndex` is set to `null`.
 *     - In Playing state: `currentTurnIndex` retains its previous value
 *       (so the diagnostic panel can stay anchored until the next turn).
 *
 *   每次 `currentTimestamp` 更新后，reducer 重新计算 `currentTurnIndex`。
 *   处于弯间时：Paused* 状态置 null；Playing 状态保留上一值。
 */

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import type { ReactNode } from 'react';
import { LoadingSubState, PlaybackState } from '../types/enums';
import type { PlaybackContext, PlaybackEvent } from '../types/state-machine';
import type { Turn } from '../types/ski-data';
import { useAnimationFrame } from '../hooks/useAnimationFrame';
import { findNextTurn, findTurnAtTimestamp } from '../hooks/useTurnBoundary';

// ===== Constants =====

/** Debounce window for USER_SEEK while playing (ms). / 播放中 seek 防抖窗口（毫秒）。 */
const SEEK_DEBOUNCE_MS = 50;

/** Single-frame step interval at 100 Hz sampling (ms). / 100Hz 采样下单帧步进间隔（毫秒）。 */
const FRAME_INTERVAL_MS = 10;

// ===== Initial State =====

/** Initial PlaybackContext before data is loaded. / 加载数据前的初始上下文。 */
const initialState: PlaybackContext = {
  state: PlaybackState.Loading,
  loadingSubState: LoadingSubState.LoadingData,
  currentTimestamp: 0,
  speedMultiplier: 1.0,
  currentTurnIndex: null,
  duration_ms: 0,
  errorMessage: null,
  lastSeekTimestamp: null,
  pendingSeek: null,
};

// ===== Helpers =====

/** Clamp `v` into the inclusive [min, max] range. / 将 v 限制在闭区间 [min, max]。 */
function clamp(v: number, min: number, max: number): number {
  if (v < min) return min;
  if (v > max) return max;
  return v;
}

/**
 * Resolve the `currentTurnIndex` for a new timestamp.
 *
 * During Playing we keep the previous index when between turns (so the panel
 * stays anchored); for Paused* states the caller passes `null` explicitly.
 *
 * 在 Playing 状态下，弯间保留上一序号；Paused* 状态由调用方显式传 null。
 *
 * @param turns        - turns array / 弯数组
 * @param timestampMs  - new timestamp / 新时间戳
 * @param previous     - previous currentTurnIndex / 上一 currentTurnIndex
 * @param keepLast     - whether to keep previous when between turns
 *                       弯间是否保留上一序号
 */
function resolveTurnIndex(
  turns: Turn[],
  timestampMs: number,
  previous: number | null,
  keepLast: boolean
): number | null {
  const found = findTurnAtTimestamp(turns, timestampMs);
  if (found != null) return found;
  return keepLast ? previous : null;
}

// ===== Pure Reducer =====

/**
 * SM-1 playback reducer (pure function).
 *
 * The `turns` array is passed as a third argument (rather than stored in
 * PlaybackContext) to keep the context serializable and to clearly separate
 * static reference data (turns) from dynamic playback state. The Provider
 * injects the latest turns via a ref closure.
 *
 * `turns` 作为第三个参数传入（不存入 PlaybackContext），以保持上下文可序列化
 * 并清晰区分静态参考数据（弯）与动态回放状态。Provider 通过 ref 闭包注入最新弯数组。
 *
 * @param state  - current PlaybackContext / 当前上下文
 * @param event  - dispatched PlaybackEvent / 派发的事件
 * @param turns  - turns array (read-only reference) / 弯数组（只读引用）
 * @returns next PlaybackContext / 下一上下文
 */
export function playbackReducer(
  state: PlaybackContext,
  event: PlaybackEvent,
  turns: Turn[]
): PlaybackContext {
  switch (event.type) {
    // ----- Loading -----
    case 'LOAD_START':
      return {
        ...state,
        loadingSubState: LoadingSubState.LoadingData,
      };

    case 'LOAD_SUCCESS':
      // Data loaded successfully → transition to Ready.
      // 数据加载成功 → 转为 Ready。
      return {
        ...state,
        state: PlaybackState.Ready,
        loadingSubState: LoadingSubState.LoadingValidate,
        duration_ms: event.duration_ms,
        currentTimestamp: 0,
        currentTurnIndex: null,
        errorMessage: null,
        pendingSeek: null,
        lastSeekTimestamp: null,
      };

    case 'LOAD_ERROR':
      // Loading failed → enter LoadingError sub-state, remain in Loading.
      // 加载失败 → 进入 LoadingError 子状态，仍处于 Loading。
      return {
        ...state,
        loadingSubState: LoadingSubState.LoadingError,
        errorMessage: event.message,
      };

    // ----- Play / Pause -----
    case 'USER_PLAY': {
      switch (state.state) {
        case PlaybackState.Ready:
          return { ...state, state: PlaybackState.Playing };
        case PlaybackState.PausedInTurn:
        case PlaybackState.PausedBetweenTurns:
          // Resume — clear any stale pending seek.
          // 恢复播放 — 清除残留的 pending seek。
          return { ...state, state: PlaybackState.Playing, pendingSeek: null };
        case PlaybackState.Ended:
          // Restart from the beginning.
          // 从头开始播放。
          return {
            ...state,
            state: PlaybackState.Playing,
            currentTimestamp: 0,
            currentTurnIndex: null,
            pendingSeek: null,
          };
        default:
          return state;
      }
    }

    case 'USER_PAUSE': {
      if (state.state !== PlaybackState.Playing) return state;
      // Pause: PausedInTurn if inside a turn, else PausedBetweenTurns.
      // 暂停：弯内 → PausedInTurn；弯间 → PausedBetweenTurns。
      if (state.currentTurnIndex != null) {
        return { ...state, state: PlaybackState.PausedInTurn };
      }
      return {
        ...state,
        state: PlaybackState.PausedBetweenTurns,
        currentTurnIndex: null,
      };
    }

    // ----- Seek -----
    case 'USER_SEEK': {
      const ts = clamp(event.timestamp, 0, state.duration_ms);
      switch (state.state) {
        case PlaybackState.Ready:
          // Immediate apply in Ready.
          // Ready 状态立即应用。
          return {
            ...state,
            currentTimestamp: ts,
            currentTurnIndex: findTurnAtTimestamp(turns, ts),
            lastSeekTimestamp: ts,
          };
        case PlaybackState.Playing:
          // Debounce: stash in pendingSeek, apply on SEEK_DEBOUNCE.
          // 防抖：暂存到 pendingSeek，待 SEEK_DEBOUNCE 时应用。
          return {
            ...state,
            pendingSeek: ts,
            lastSeekTimestamp: ts,
          };
        case PlaybackState.PausedInTurn:
          // Immediate apply; recompute turn index.
          // 立即应用；重新计算弯序号。
          return {
            ...state,
            currentTimestamp: ts,
            currentTurnIndex: findTurnAtTimestamp(turns, ts),
            lastSeekTimestamp: ts,
          };
        case PlaybackState.PausedBetweenTurns:
          // Immediate apply; between turns → null.
          // 立即应用；弯间 → null。
          return {
            ...state,
            currentTimestamp: ts,
            currentTurnIndex: null,
            lastSeekTimestamp: ts,
          };
        case PlaybackState.Ended:
          // Seek away from the end → return to Ready.
          // 从结束位置 seek 到非末尾 → 回到 Ready。
          if (ts < state.duration_ms) {
            return {
              ...state,
              state: PlaybackState.Ready,
              currentTimestamp: ts,
              currentTurnIndex: findTurnAtTimestamp(turns, ts),
              lastSeekTimestamp: ts,
            };
          }
          return state;
        default:
          return state;
      }
    }

    case 'SEEK_DEBOUNCE': {
      // Apply the pending seek (only relevant in Playing).
      // 应用挂起的 seek（仅在 Playing 中有意义）。
      if (state.pendingSeek == null) return state;
      if (state.state !== PlaybackState.Playing) {
        // Stale debounce — clear without applying.
        // 过期防抖 — 清除但不应用。
        return { ...state, pendingSeek: null };
      }
      const ts = state.pendingSeek;
      return {
        ...state,
        currentTimestamp: ts,
        pendingSeek: null,
        // During Playing, keep last currentTurnIndex when between turns.
        // Playing 状态下弯间保留上一 currentTurnIndex。
        currentTurnIndex: resolveTurnIndex(turns, ts, state.currentTurnIndex, true),
      };
    }

    // ----- Frame Update -----
    case 'FRAME_UPDATE': {
      if (state.state !== PlaybackState.Playing) return state;
      // While a seek is pending, ignore frame updates to avoid fighting
      // the debounce timer.
      // 有挂起的 seek 时忽略帧更新，避免与防抖定时器冲突。
      if (state.pendingSeek != null) return state;
      const ts = Math.min(event.timestamp, state.duration_ms);
      return {
        ...state,
        currentTimestamp: ts,
        currentTurnIndex: resolveTurnIndex(turns, ts, state.currentTurnIndex, true),
      };
    }

    // ----- End -----
    case 'PLAYBACK_ENDED':
      if (state.state !== PlaybackState.Playing) return state;
      return {
        ...state,
        state: PlaybackState.Ended,
        currentTimestamp: state.duration_ms,
        pendingSeek: null,
      };

    // ----- Tab Visibility -----
    case 'TAB_HIDDEN':
      // Per spec: when the tab is hidden during Playing, pause and cancel rAF.
      // The rAF cancellation is handled by useAnimationFrame; here we just
      // transition the state so isPlaying becomes false.
      // 按规格：标签页隐藏时暂停并取消 rAF。rAF 取消由 useAnimationFrame 处理，
      // 此处仅转移状态使 isPlaying 变为 false。
      if (state.state !== PlaybackState.Playing) return state;
      if (state.currentTurnIndex != null) {
        return { ...state, state: PlaybackState.PausedInTurn };
      }
      return {
        ...state,
        state: PlaybackState.PausedBetweenTurns,
        currentTurnIndex: null,
      };

    case 'TAB_VISIBLE':
      // Per spec: do not auto-resume when the tab becomes visible again.
      // The user must press play manually.
      // 按规格：标签页恢复可见时不自动恢复播放，需用户手动按播放。
      return state;

    // ----- Step Frame -----
    case 'USER_STEP_FRAME': {
      if (
        state.state === PlaybackState.Loading ||
        state.state === PlaybackState.Ended
      ) {
        return state;
      }
      // Step by one frame interval (10 ms at 100 Hz).
      // 按单帧间隔步进（100Hz 下 10ms）。
      const delta = event.direction * FRAME_INTERVAL_MS;
      const ts = clamp(state.currentTimestamp + delta, 0, state.duration_ms);
      return {
        ...state,
        currentTimestamp: ts,
        currentTurnIndex: findTurnAtTimestamp(turns, ts),
      };
    }

    // ----- Jump Turn -----
    case 'USER_JUMP_TURN': {
      if (
        state.state === PlaybackState.Loading ||
        state.state === PlaybackState.Ended
      ) {
        return state;
      }
      const nextTurn = findNextTurn(turns, state.currentTimestamp, event.direction);
      if (!nextTurn) return state;
      const ts = clamp(nextTurn.start_ms, 0, state.duration_ms);
      return {
        ...state,
        currentTimestamp: ts,
        currentTurnIndex: findTurnAtTimestamp(turns, ts),
      };
    }

    // ----- Speed -----
    case 'USER_SET_SPEED':
      return { ...state, speedMultiplier: event.speed };

    // ----- Turn enter/exit (external signaling) -----
    case 'TURN_ENTER':
      return { ...state, currentTurnIndex: event.turnIndex };

    case 'TURN_EXIT':
      // Only null out when paused between turns; during Playing we keep
      // the last index (see resolveTurnIndex).
      // 仅在 PausedBetweenTurns 时置 null；Playing 中保留上一序号。
      if (state.state === PlaybackState.PausedBetweenTurns) {
        return { ...state, currentTurnIndex: null };
      }
      return state;

    default:
      return state;
  }
}

// ===== Public API =====

/**
 * Public API surface returned by {@link usePlaybackMachine}.
 *
 * Exposes the full PlaybackContext (state), the raw dispatch, and
 * convenience methods that wrap common events.
 */
export interface PlaybackMachineApi {
  /** Current playback context. / 当前回放上下文。 */
  state: PlaybackContext;
  /** Raw dispatch for advanced event usage. / 原始派发函数，用于高级事件。 */
  dispatch: (event: PlaybackEvent) => void;
  // Convenience methods / 便捷方法
  /** USER_PLAY */
  play: () => void;
  /** USER_PAUSE */
  pause: () => void;
  /** USER_SEEK */
  seek: (timestamp: number) => void;
  /** USER_STEP_FRAME */
  stepFrame: (direction: 1 | -1) => void;
  /** USER_JUMP_TURN */
  jumpTurn: (direction: 1 | -1) => void;
  /** USER_SET_SPEED */
  setSpeed: (speed: 0.5 | 1.0) => void;
  /** LOAD_SUCCESS (also stashes turns for the reducer) */
  loadSuccess: (duration_ms: number, turns: Turn[]) => void;
  /** LOAD_ERROR */
  loadError: (message: string) => void;
}

// ===== Context =====

/**
 * Internal React context holding the {@link PlaybackMachineApi}.
 *
 * Using `null` as the default lets the consumer hook detect missing
 * Providers and throw a helpful error.
 *
 * 内部 React context，持有 {@link PlaybackMachineApi}。
 * 默认值为 null 以便消费方检测缺失的 Provider 并抛出友好错误。
 */
const PlaybackMachineContext = createContext<PlaybackMachineApi | null>(null);

// ===== Provider =====

/**
 * Provider component that owns the SM-1 reducer and manages all side effects:
 * rAF scheduling (via {@link useAnimationFrame}), `visibilitychange` handling,
 * and seek debounce.
 *
 * 拥有 SM-1 reducer 并管理所有副作用的 Provider 组件：
 * rAF 调度（via {@link useAnimationFrame}）、visibilitychange 监听、seek 防抖。
 *
 * @param props.children - subtree to receive the playback API / 接收回放 API 的子树
 */
export function PlaybackMachineProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  // turnsRef holds the latest turns array so the reducer (a pure function)
  // can access it without it being part of PlaybackContext.
  // turnsRef 持有最新弯数组，使纯函数 reducer 可访问而无需放入 PlaybackContext。
  const turnsRef = useRef<Turn[]>([]);

  // stateRef mirrors the latest state for use inside the rAF onFrame callback,
  // which would otherwise capture a stale closure.
  // stateRef 镜像最新状态，供 rAF onFrame 回调读取，避免闭包过期。
  const stateRef = useRef<PlaybackContext>(initialState);

  // Wrap the pure reducer to inject turnsRef.current. The wrapper identity
  // is stable (useCallback with []), so useReducer won't reinitialize.
  // 包装纯 reducer 以注入 turnsRef.current。包装函数身份稳定（useCallback []），
  // useReducer 不会重新初始化。
  const wrappedReducer = useCallback(
    (state: PlaybackContext, event: PlaybackEvent): PlaybackContext =>
      playbackReducer(state, event, turnsRef.current),
    []
  );

  const [state, dispatch] = useReducer(wrappedReducer, initialState);

  // Keep stateRef in sync on every render so the rAF callback always reads
  // the freshest state. This is the standard "latest state ref" pattern.
  // 每次渲染同步 stateRef，确保 rAF 回调读到最新状态（标准"最新状态 ref"模式）。
  stateRef.current = state;

  // --- Side effect: rAF scheduling ---
  // 副作用：rAF 调度
  const handleFrame = useCallback((deltaMs: number) => {
    const s = stateRef.current;
    if (s.state !== PlaybackState.Playing) return;
    // While a seek is pending, skip frame updates — the debounce timer
    // will apply the final position.
    // 有挂起的 seek 时跳过帧更新 — 防抖定时器会应用最终位置。
    if (s.pendingSeek != null) return;
    const next = s.currentTimestamp + deltaMs;
    if (next >= s.duration_ms) {
      dispatch({ type: 'PLAYBACK_ENDED' });
    } else {
      dispatch({ type: 'FRAME_UPDATE', timestamp: next });
    }
  }, []);

  const handleEnd = useCallback(() => {
    dispatch({ type: 'PLAYBACK_ENDED' });
  }, []);

  useAnimationFrame({
    isPlaying: state.state === PlaybackState.Playing,
    speedMultiplier: state.speedMultiplier,
    maxTimestamp: state.duration_ms,
    onFrame: handleFrame,
    onEnd: handleEnd,
  });

  // --- Side effect: visibilitychange ---
  // 副作用：标签页可见性变化
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        dispatch({ type: 'TAB_HIDDEN' });
      }
      // TAB_VISIBLE is a no-op per spec (no auto-resume).
      // 按规格 TAB_VISIBLE 为空操作（不自动恢复）。
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // --- Side effect: seek debounce ---
  // 副作用：seek 防抖
  // When pendingSeek is set (USER_SEEK in Playing), schedule a SEEK_DEBOUNCE
  // after 50ms. If another USER_SEEK arrives, the previous effect cleanup
  // clears the timer and a new one starts — only the final position is applied.
  //
  // 当 pendingSeek 被设置（Playing 中 USER_SEEK）时，50ms 后调度 SEEK_DEBOUNCE。
  // 若又收到 USER_SEEK，上一次 effect 清理会清除定时器并重新计时 —
  // 最终仅应用最后一次位置。
  useEffect(() => {
    if (state.pendingSeek == null) return;
    const timer = setTimeout(() => {
      dispatch({ type: 'SEEK_DEBOUNCE' });
    }, SEEK_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [state.pendingSeek]);

  // --- Convenience methods ---
  // 便捷方法
  const play = useCallback(() => dispatch({ type: 'USER_PLAY' }), []);
  const pause = useCallback(() => dispatch({ type: 'USER_PAUSE' }), []);
  const seek = useCallback(
    (timestamp: number) => dispatch({ type: 'USER_SEEK', timestamp }),
    []
  );
  const stepFrame = useCallback(
    (direction: 1 | -1) => dispatch({ type: 'USER_STEP_FRAME', direction }),
    []
  );
  const jumpTurn = useCallback(
    (direction: 1 | -1) => dispatch({ type: 'USER_JUMP_TURN', direction }),
    []
  );
  const setSpeed = useCallback(
    (speed: 0.5 | 1.0) => dispatch({ type: 'USER_SET_SPEED', speed }),
    []
  );
  const loadSuccess = useCallback(
    (duration_ms: number, turns: Turn[]) => {
      turnsRef.current = turns;
      dispatch({ type: 'LOAD_SUCCESS', duration_ms, turns });
    },
    []
  );
  const loadError = useCallback(
    (message: string) => dispatch({ type: 'LOAD_ERROR', message }),
    []
  );

  // Assemble the API object. Memoized so consumers don't get a new object
  // identity on every render unless `state` actually changed.
  // 组装 API 对象。使用 useMemo 防止每次渲染都产生新对象引用。
  const api = useMemo<PlaybackMachineApi>(
    () => ({
      state,
      dispatch,
      play,
      pause,
      seek,
      stepFrame,
      jumpTurn,
      setSpeed,
      loadSuccess,
      loadError,
    }),
    [
      state,
      dispatch,
      play,
      pause,
      seek,
      stepFrame,
      jumpTurn,
      setSpeed,
      loadSuccess,
      loadError,
    ]
  );

  // createElement (instead of JSX) keeps this a .ts file.
  // 使用 createElement 而非 JSX，以保持 .ts 文件扩展名。
  return createElement(PlaybackMachineContext.Provider, { value: api }, children);
}

// ===== Consumer Hook =====

/**
 * Consume the SM-1 playback machine API. Must be called within a
 * {@link PlaybackMachineProvider} subtree.
 *
 * 消费 SM-1 回放状态机 API。必须在 {@link PlaybackMachineProvider} 子树内调用。
 *
 * @returns the playback API (see {@link PlaybackMachineApi})
 * @throws if called outside a Provider
 */
export function usePlaybackMachine(): PlaybackMachineApi {
  const ctx = useContext(PlaybackMachineContext);
  if (ctx == null) {
    throw new Error(
      'usePlaybackMachine must be used within a <PlaybackMachineProvider>.'
    );
  }
  return ctx;
}
